import React, { useState, useEffect, useRef } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const PaymentForm = ({ amount, onSuccess, customerInfo }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [showTimeoutActions, setShowTimeoutActions] = useState(false);
  
  const gotResponseRef = useRef(false);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      if (mountedRef.current) setError(null);
    };
    const handleOffline = () => {
      if (mountedRef.current) setError('You are offline. Please check your internet connection.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:contact@dearbea.com.au?subject=Payment%20Issue';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !ready) {
      return;
    }

    if (!navigator.onLine) {
      setError('No internet connection. Please check your network and try again.');
      return;
    }

    setProcessing(true);
    setError(null);
    setShowTimeoutActions(false);
    gotResponseRef.current = false;

    // Timeout handler - shows escape options but keeps processing true
    timeoutRef.current = setTimeout(() => {
      if (!gotResponseRef.current && mountedRef.current) {
        setError('Payment is taking longer than expected. If you were charged, do NOT try again.');
        setShowTimeoutActions(true);
        // Keep processing=true to prevent double submission
      }
    }, 30000);

    try {
      console.log('🔵 Starting payment confirmation...');
      
      // Confirm payment
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/order-confirmation',
          payment_method_data: {
            billing_details: {
              name: `${customerInfo.firstName} ${customerInfo.lastName}`,
              email: customerInfo.email,
              phone: customerInfo.phone,
              address: {
                line1: customerInfo.address || '',
                city: customerInfo.suburb || '',
                postal_code: customerInfo.postcode || '',
                country: 'AU',
              },
            },
          },
        },
        redirect: 'if_required',
      });

      // ✅ FIX 1: Set gotResponseRef IMMEDIATELY after confirmPayment returns
      gotResponseRef.current = true;

      // Now destructure the result
      const { error: submitError, paymentIntent } = result;

      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // Check if unmounted
      if (!mountedRef.current) {
        console.log('⚠️ Component unmounted during payment');
        return;
      }

      // Handle errors
      if (submitError) {
        console.error('❌ Payment error:', {
          type: submitError.type,
          code: submitError.code,
          decline_code: submitError.decline_code,
          message: submitError.message
        });
        
        if (submitError.type === 'card_error') {
          setError(submitError.message);
        } else if (submitError.type === 'validation_error') {
          setError('Please check your card details and try again.');
        } else if (submitError.type === 'api_connection_error' || submitError.code === 'network_error') {
          setError('Network connection error. Please check your internet and try again.');
        } else if (submitError.code === 'payment_intent_unexpected_state') {
          setError('This payment is already being processed. Please refresh the page.');
        } else {
          setError(submitError.message || 'Payment failed. Please try again.');
        }
        
        setProcessing(false);
        return;
      }

      if (!paymentIntent) {
        console.error('❌ No payment intent returned');
        setError('No payment response received. Please try again or contact support.');
        setProcessing(false);
        return;
      }

      console.log('💳 Payment Intent status:', paymentIntent.status);
      console.log('💳 Payment ID:', paymentIntent.id);

      // Handle payment statuses
      if (paymentIntent.status === 'succeeded') {
        console.log('✅ Payment succeeded');
        setProcessing(false);
        onSuccess(paymentIntent.id);
        return;
      }

      if (paymentIntent.status === 'processing') {
        console.log('⏳ Payment is processing - order will be marked as pending');
        setProcessing(false);
        onSuccess(paymentIntent.id);
        return;
      }

      if (paymentIntent.status === 'requires_capture') {
        console.log('⏸️ Payment requires capture (manual mode)');
        setProcessing(false);
        onSuccess(paymentIntent.id);
        return;
      }

      if (paymentIntent.status === 'requires_payment_method') {
        console.log('❌ Payment failed - requires new payment method');
        setError('Payment failed. Please try a different card or payment method.');
        setProcessing(false);
        return;
      }

      if (paymentIntent.status === 'requires_action') {
        console.log('🔐 Requires additional action (unexpected)');
        setError('Additional authentication is required. Please refresh and try again.');
        setProcessing(false);
        return;
      }

      if (paymentIntent.status === 'requires_confirmation') {
        console.log('⚠️ Requires confirmation');
        setError('Payment requires confirmation. Please contact support.');
        setProcessing(false);
        return;
      }

      if (paymentIntent.status === 'canceled') {
        console.log('🚫 Payment was canceled');
        setError('Payment was canceled. Please try again.');
        setProcessing(false);
        return;
      }

      console.warn('⚠️ Unexpected payment status:', paymentIntent.status);
      setError(`Unexpected payment status: ${paymentIntent.status}. Please contact support if you were charged.`);
      setProcessing(false);

    } catch (error) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      gotResponseRef.current = true;

      if (!mountedRef.current) return;

      console.error('❌ Exception during payment:', error);
      
      if (!navigator.onLine) {
        setError('You appear to be offline. Please check your internet connection.');
      } else if (error.message && error.message.toLowerCase().includes('network')) {
        setError('Network error occurred. Please try again.');
      } else {
        setError('An unexpected error occurred. Please try again or contact support.');
      }
      
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
        <PaymentElement
          onReady={() => {
            console.log('✅ Payment Element ready');
            if (mountedRef.current) setReady(true);
          }}
          options={{
            layout: {
              type: 'tabs',
              defaultCollapsed: false,
            },
            fields: {
              billingDetails: {
                address: {
                  country: 'never',
                },
              },
            },
          }}
        />
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="font-semibold text-red-800">Payment Error</p>
              <p className="text-red-700 text-sm">{error}</p>
              
              {/* ✅ FIX 2: Timeout escape hatch */}
              {showTimeoutActions && (
                <div className="mt-4 space-y-2">
                  <button
                    type="button"
                    onClick={handleRefresh}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg text-sm font-semibold transition"
                  >
                    Refresh Page
                  </button>
                  <button
                    type="button"
                    onClick={handleContactSupport}
                    className="w-full bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg text-sm font-semibold transition"
                  >
                    Contact Support
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl p-6 border-2 border-rose-200">
        <div className="flex items-center gap-3 mb-2">
          <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <p className="font-semibold text-gray-900">Secure Payment</p>
        </div>
        <p className="text-sm text-gray-700">
          Your payment information is encrypted and secure. We use Stripe to process payments.
        </p>
      </div>

      <button
        type="submit"
        disabled={!stripe || !ready || processing}
        className={`w-full py-5 rounded-xl text-xl font-bold transition-all duration-300 shadow-lg ${
          (!stripe || !ready || processing)
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-rose-600 hover:bg-rose-700 hover:scale-105'
        } text-white`}
      >
        {!ready ? (
          'Loading payment form...'
        ) : processing ? (
          <span className="flex items-center justify-center gap-3">
            <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Processing...
          </span>
        ) : (
          `Pay $${amount.toFixed(2)}`
        )}
      </button>
    </form>
  );
};

export default PaymentForm;